import React, { useState } from "react";
import ExhibitorCard from "../../../../widgets/ExhibitorCard";
import {
  Box,
  Grid,
  Pagination,
  useMediaQuery,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Typography,
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import CustomCircularP from "../../../../widgets/CustomCircularP";
import AlphabetSortWidget from "./AlphabetSortWidget";
import ExhibitorFilterBar from "./ExhibitorFilterBar";
import useApiEp from "../../../../hooks/useApiEp";

const Exhibitors = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("sm"));

  // States
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    hall: "",
    country: "",
    cat: "",
  });

  // Fetch Data
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-exhibitors?page=${page}&sq=${filters.search}&hall=${filters.hall}&country=${filters.country}&letter=${selectedLetter}&cat=${filters.cat}`
  );

  // --- Handlers ---
  const onPageChange = (e, newPage) => {
    setPage(newPage);
  };

  const handleSearch = (searchTerm) => {
    setFilters((prev) => ({ ...prev, search: searchTerm }));
    setPage(1);
  };

  const handleFilter = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      hall: newFilters.hall !== undefined ? newFilters.hall : prev.hall,
      country:
        newFilters.country !== undefined ? newFilters.country : prev.country,
      cat: newFilters.cat !== undefined ? newFilters.cat : prev.cat,
    }));
    setPage(1);
  };

  const handleLetterSelect = (newV) => {
    setSelectedLetter(newV || "");
    setPage(1);
  };

  // --- Helpers ---
  const totalPages = data?.totalItems ? Math.ceil(data.totalItems / 30) : 0;
  const hasExhibitors = data?.data && data.data.length > 0;

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <CustomCircularP show={isLoading} />

      <div>
        <ExhibitorFilterBar onSearch={handleSearch} onFilter={handleFilter} />

        <Stack
          direction="row"
          justifyContent={isLargeScreen ? "space-between" : "flex-end"}
          alignItems="center"
          mb={2}
        >
          {isLargeScreen && (
            <AlphabetSortWidget onSelectLetter={handleLetterSelect} />
          )}

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) setViewMode(newMode);
            }}
            aria-label="view mode"
            size="small"
            sx={{ backgroundColor: "#fff" }}
          >
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* List Content */}
        {!isLoading && !hasExhibitors ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="30vh"
          >
            <Typography variant="h6" color="textSecondary">
              No exhibitors found matching your criteria.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {data?.data?.map((item, index) => (
              <Grid
                item
                xs={12}
                sm={viewMode === "grid" ? 6 : 12}
                md={viewMode === "grid" ? 4 : 12}
                lg={viewMode === "grid" ? 3 : 12}
                key={item.id || index}
              >
                <ExhibitorCard item={item} viewMode={viewMode} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box mt={4} mb={2} justifyContent="center" display="flex">
            <Pagination
              count={totalPages}
              page={page}
              onChange={onPageChange}
              color="primary"
            />
          </Box>
        )}
      </div>
    </Box>
  );
};

export default Exhibitors;
